import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Megaphone, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { toast } from '@/store/toastStore';
import { listUsers } from '@/services/admin.service';
import {
  createKbAnnouncement,
  deleteKbAnnouncement,
  kbQueryKeys,
  listKbAnnouncements,
  listKnowledgeBaseDocuments,
} from '@/services/knowledge-base.service';

export function AnnouncementsPage() {
  const queryClient = useQueryClient();
  const [showCompose, setShowCompose] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [docId, setDocId] = useState<number | ''>('');
  const [docSearch, setDocSearch] = useState('');
  const [audience, setAudience] = useState<'all' | 'users'>('all');
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [requiresAck, setRequiresAck] = useState(false);

  const announcementsQuery = useQuery({
    queryKey: kbQueryKeys.announcements(),
    queryFn: () => listKbAnnouncements(),
  });

  const docsQuery = useQuery({
    queryKey: kbQueryKeys.documents({ q: docSearch.trim() || undefined, perPage: 25 }),
    queryFn: () => listKnowledgeBaseDocuments({ q: docSearch.trim() || undefined, perPage: 25 }),
    enabled: showCompose,
  });

  const usersQuery = useQuery({
    queryKey: ['kb', 'users-pick'],
    queryFn: listUsers,
    enabled: showCompose && audience === 'users',
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createKbAnnouncement({
        title: title.trim(),
        body,
        kbDocumentId: docId || null,
        audience,
        audienceRef: audience === 'users' ? { userIds: selectedUserIds } : undefined,
        requiresAck,
        publishNow: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.announcements() });
      toast.success('Announcement published');
      setShowCompose(false);
      setTitle('');
      setBody('');
      setDocId('');
      setSelectedUserIds([]);
      setRequiresAck(false);
      setAudience('all');
    },
    onError: () => toast.error('Failed to publish'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteKbAnnouncement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.announcements() });
      toast.success('Announcement deleted');
      setConfirmDelete(null);
    },
  });

  function handlePublish() {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (audience === 'users' && selectedUserIds.length === 0) {
      toast.error('Pick at least one user when audience is "Specific users"');
      return;
    }
    createMutation.mutate();
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowCompose(true)}>
          New Announcement
        </Button>
      </div>

      {announcementsQuery.isLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">Loading...</div>
      ) : !announcementsQuery.data?.data.length ? (
        <EmptyState
          icon={<Megaphone className="h-8 w-8" />}
          title="No announcements yet"
          description="Push must-read updates to all staff or specific users."
          action={{ label: 'New Announcement', onClick: () => setShowCompose(true) }}
        />
      ) : (
        <div className="space-y-2">
          {announcementsQuery.data.data.map((a) => (
            <div key={a.kbAnnouncementId} className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-slate-900">{a.title}</h3>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <Badge variant="info">{a.audience}</Badge>
                    {a.requiresAck && <Badge variant="warning">Requires acknowledgement</Badge>}
                    {a.publishedAt && <span>{new Date(a.publishedAt).toLocaleString()}</span>}
                  </div>
                  {a.body && <p className="mt-2 line-clamp-2 text-sm text-slate-600">{a.body}</p>}
                </div>
                <Button
                  size="xs"
                  variant="ghost"
                  aria-label="Delete"
                  onClick={() => setConfirmDelete(a.kbAnnouncementId)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
              <div className="mt-3 flex gap-4 text-xs text-slate-600">
                <span>
                  <strong>{a.recipientCount ?? 0}</strong> recipients
                </span>
                <span>
                  <strong>{a.seenCount ?? 0}</strong> seen
                </span>
                <span>
                  <strong>{a.ackCount ?? 0}</strong> acknowledged
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={showCompose}
        onClose={() => setShowCompose(false)}
        title="New Announcement"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCompose(false)}>
              Cancel
            </Button>
            <Button onClick={handlePublish} loading={createMutation.isPending}>
              Publish
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Title" required value={title} onChange={(e) => setTitle(e.target.value)} />
          <Textarea label="Body" value={body} onChange={(e) => setBody(e.target.value)} rows={5} />

          <FormField label="Link to a document (optional)">
            <Input
              placeholder="Search documents..."
              value={docSearch}
              onChange={(e) => setDocSearch(e.target.value)}
            />
            <div className="mt-2 max-h-[140px] overflow-y-auto rounded-md border border-slate-200">
              <button
                type="button"
                onClick={() => setDocId('')}
                className={`block w-full px-3 py-1.5 text-left text-sm ${
                  docId === '' ? 'bg-blue-50 font-medium text-blue-700' : 'hover:bg-slate-50'
                }`}
              >
                None
              </button>
              {docsQuery.data?.data.map((d) => (
                <button
                  key={d.kbDocumentId}
                  type="button"
                  onClick={() => setDocId(d.kbDocumentId)}
                  className={`block w-full truncate px-3 py-1.5 text-left text-sm ${
                    docId === d.kbDocumentId ? 'bg-blue-50 font-medium text-blue-700' : 'hover:bg-slate-50'
                  }`}
                >
                  {d.title}
                </button>
              ))}
            </div>
          </FormField>

          <FormField label="Audience">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setAudience('all')}
                className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                  audience === 'all'
                    ? 'border-blue-500 bg-blue-50 font-medium text-blue-700'
                    : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
              >
                All staff
              </button>
              <button
                type="button"
                onClick={() => setAudience('users')}
                className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                  audience === 'users'
                    ? 'border-blue-500 bg-blue-50 font-medium text-blue-700'
                    : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
              >
                Specific users
              </button>
            </div>
          </FormField>

          {audience === 'users' && (
            <div className="max-h-[180px] overflow-y-auto rounded-md border border-slate-200">
              {(usersQuery.data ?? []).filter((u) => u.active).map((u) => (
                <label
                  key={u.userId}
                  className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    checked={selectedUserIds.includes(u.userId)}
                    onChange={() =>
                      setSelectedUserIds((curr) =>
                        curr.includes(u.userId) ? curr.filter((id) => id !== u.userId) : [...curr, u.userId],
                      )
                    }
                  />
                  <span>{`${u.firstName} ${u.lastName}`.trim() || u.userName}</span>
                </label>
              ))}
            </div>
          )}

          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={requiresAck}
              onChange={(e) => setRequiresAck(e.target.checked)}
            />
            Require explicit acknowledgement
          </label>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete !== null) deleteMutation.mutate(confirmDelete);
        }}
        title="Delete announcement?"
        message="The announcement and all its read/ack records will be removed."
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}
